import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  ColumnPinningState,
  PaginationState,
} from '@tanstack/react-table';
import type { RowSelectionState } from '@tanstack/react-table';
import { ColumnFilterProps, OperatorOption, MenuCoords, TanStackGridProps } from './types';
import { getAllColumns } from './columns/index';
import EvidenceTimelineColumnFilter from './components/EvidenceTimelineColumnFilter';
import GtpColumnFilter from './components/GtpColumnFilter';
import PopupMessage from './components/PopupMessage';
import RowAidaQuestionsButton from './components/RowAidaQuestionsButton';
import { TableHeaderDownload } from './components/TableHeaderDownload';
import { userAppContext } from '@/context/Context';
import { ChevronRightIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { trackEvent } from '@/utils/analytics';
import { dropdownFilterMatchesSearch } from '@/utils/associationTypeOptions';
import { useRightSidebarLayout } from '@/modules/search/searchGrid/context/RightSidebarLayoutContext';
import { RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX } from '@/modules/search/searchGrid/utils/searchResultsHelpers';
import { OPEN_GTP_ROW_DETAIL_FOR_GENE_EVENT, rowMatchesGeneId } from '@/utils/openGtpRowDetail';
import { getGtpItemsFromRow } from '@/utils/formatGtpList';
import { getRowGeneNcbiId, isNcbiIdColumn } from '@/utils/geneNcbiId';
import { isEventInsideDemoTourTip } from '@/pages/productDemo/productDemoTourHelpers';

function visibilityRecordsEqual(a: Record<string, boolean> | undefined, b: Record<string, boolean>): boolean {
  if (!a) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(key => a[key] === b[key]);
}

const MULTI_SELECT_COLUMN_IDS = new Set([
  'chemical_effect',
  'diseaseClassName_HPO',
  'disease_inheritance',
  'ancestry_name',
  'disease_model_in_doc',
  'sex_in_doc',
  'GTP',
  'mostSevereConsequences',
  'clinical_significance',
  'DBSNPclass',
  'associationType',
  'diseaseClassName_DO',
  'diseaseClassName_MSH',
  'geneProteinClassNames',
  'geneNcbiType',
  'ncbi_type',
  'chemMoleculeType',
  'disease1ClassName_DO',
  'disease2ClassName_DO',
  'disease2ClassName_HPO',
  'disease1ClassName_HPO',
  'disease2ClassName_MSH',
  'disease1ClassName_MSH',
  'ddaRelation',
  'nct_phase',
]);
const MAX_PINNED_COLUMNS = 3;

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & React.InputHTMLAttributes<HTMLInputElement>) {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (typeof indeterminate === 'boolean' && ref.current) {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [indeterminate, rest.checked]);
  return <input type="checkbox" ref={ref} className={className + ' cursor-pointer'} {...rest} />;
}

// Column Filter Component
function ColumnFilter({
  columnId,
  filterType,
  value,
  operator,
  onValueChange,
  onOperatorChange,
  dropdownOptions,
  emptiness,
  onEmptinessChange,
}: ColumnFilterProps) {
  const [showOperatorMenu, setShowOperatorMenu] = useState<boolean>(false);
  const [showDropdownMenu, setShowDropdownMenu] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<string[]>(() => {
    return value ? value.split(',').filter(Boolean) : [];
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);

  // Enable filter operator options for text fields that support contains/equals
  const supportsContains = [
    'geneSymbol',
    'gene_symbol',
    'chem_name',
    'diseaseName',
    'geneDescription',
    'disease1_Name',
    'disease2_Name',
    'name',
    'chemgda',
    'chemvda',
    'ChemPrefName',
    'chemPrefName',
    'chemsInSentence',
    'symbol',
    'description',
    'hgvsc',
    'hgvsp',
    'pmed_title',
  ].includes(columnId);

  const supportsEmptinessFilter = [
    'chemsInSentence',
    'disease_model_in_doc',
    'sex_in_doc',
    'ancestry_name',
  ].includes(columnId);

  const operatorOptions: OperatorOption[] =
    filterType === 'number'
      ? [
          { value: 'eq', label: 'Equals' },
          { value: 'gt', label: 'Greater than' },
          { value: 'lt', label: 'Less than' },
          { value: 'gte', label: 'Greater or equal' },
          { value: 'lte', label: 'Less or equal' },
        ]
      : supportsContains
        ? [
            { value: 'eq', label: 'Equals' },
            { value: 'contains', label: 'Contains' },
          ]
        : [{ value: 'eq', label: 'Equals' }];

  const defaultOperator = filterType === 'number' ? 'eq' : supportsContains ? 'eq' : 'eq';
  const currentOperator = operator || defaultOperator;

  useEffect(() => {
    const newSelectedItems = value ? value.split(',').filter(Boolean) : [];
    if (JSON.stringify(newSelectedItems) !== JSON.stringify(selectedItems)) {
      setSelectedItems(newSelectedItems);
    }
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowOperatorMenu(false);
      }
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        dropdownButtonRef.current &&
        !dropdownButtonRef.current.contains(event.target as Node)
      ) {
        setShowDropdownMenu(false);
        setSearchTerm('');
      }
    }

    function updatePosition() {
      if (showOperatorMenu && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceOnLeft = rect.left;
        const position = spaceOnLeft < 180 ? 'right' : 'left';

        setMenuCoords({
          top: rect.bottom + 4,
          left: position === 'left' ? rect.right - 160 : rect.left,
        });
      }

      if (showDropdownMenu && dropdownButtonRef.current) {
        const rect = dropdownButtonRef.current.getBoundingClientRect();
        setDropdownCoords({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
    }

    if (showOperatorMenu || showDropdownMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showOperatorMenu, showDropdownMenu]);

  const [menuCoords, setMenuCoords] = useState<MenuCoords>({ top: 0, left: 0 });
  const [dropdownCoords, setDropdownCoords] = useState<MenuCoords>({ top: 0, left: 0 });

  useEffect(() => {
    if (showOperatorMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceOnLeft = rect.left;

      const position = spaceOnLeft < 180 ? 'right' : 'left';

      setMenuCoords({
        top: rect.bottom + 4,
        left: position === 'left' ? rect.right - 160 : rect.left,
      });
    }
  }, [showOperatorMenu]);

  useEffect(() => {
    if (showDropdownMenu && dropdownButtonRef.current) {
      const rect = dropdownButtonRef.current.getBoundingClientRect();
      setDropdownCoords({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [showDropdownMenu]);

  const handleToggleItem = (itemId: string) => {
    const newSelectedItems = isMultiSelectColumn
      ? selectedItems.includes(itemId)
        ? selectedItems.filter(id => id !== itemId)
        : [...selectedItems, itemId]
      : selectedItems.includes(itemId)
        ? []
        : [itemId];
    setSelectedItems(newSelectedItems);
    onValueChange(newSelectedItems.join(','));

    if (!isMultiSelectColumn) {
      setShowDropdownMenu(false);
      setSearchTerm('');
    }
  };

  const filteredDropdownOptions =
    dropdownOptions?.filter(option => dropdownFilterMatchesSearch(option, searchTerm)) || [];
  const isMultiSelectColumn = MULTI_SELECT_COLUMN_IDS.has(columnId);
  const selectedItemsSet = useMemo(() => new Set(selectedItems), [selectedItems]);
  const filteredOptionIds = useMemo(
    () => filteredDropdownOptions.map(option => option.id),
    [filteredDropdownOptions],
  );
  const allFilteredSelected =
    filteredOptionIds.length > 0 && filteredOptionIds.every(optionId => selectedItemsSet.has(optionId));
  const someFilteredSelected =
    filteredOptionIds.some(optionId => selectedItemsSet.has(optionId)) && !allFilteredSelected;

  const handleToggleSelectAll = () => {
    if (filteredOptionIds.length === 0) return;

    const newSelectedItems = allFilteredSelected
      ? selectedItems.filter(itemId => !filteredOptionIds.includes(itemId))
      : Array.from(new Set([...selectedItems, ...filteredOptionIds]));

    setSelectedItems(newSelectedItems);
    onValueChange(newSelectedItems.join(','));
  };

  const renderMenu = () => {
    if (!showOperatorMenu) return null;

    return ReactDOM.createPortal(
      <div
        ref={menuRef}
        className="filter-operator-menu-portal"
        style={{
          position: 'fixed',
          top: `${menuCoords.top}px`,
          left: `${menuCoords.left}px`,
          zIndex: 99999,
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          minWidth: '160px',
          overflow: 'hidden',
        }}
      >
        {operatorOptions.map(opt => (
          <div
            key={opt.value}
            className={`filter-operator-option ${currentOperator === opt.value && !emptiness ? 'active' : ''}`}
            onClick={() => {
              onOperatorChange(opt.value);
              setShowOperatorMenu(false);
            }}
          >
            {opt.label}
          </div>
        ))}
        {supportsEmptinessFilter && onEmptinessChange && (
          <>
            <div style={{ borderTop: '1px solid #ddd', margin: '4px 0' }} />
            <div
              className={`filter-operator-option ${emptiness === 'not_empty' ? 'active' : ''}`}
              onClick={() => {
                onEmptinessChange(emptiness === 'not_empty' ? '' : 'not_empty');
                setShowOperatorMenu(false);
              }}
            >
              Not empty
            </div>
            <div
              className={`filter-operator-option ${emptiness === 'is_empty' ? 'active' : ''}`}
              onClick={() => {
                onEmptinessChange(emptiness === 'is_empty' ? '' : 'is_empty');
                setShowOperatorMenu(false);
              }}
            >
              Is empty
            </div>
          </>
        )}
      </div>,
      document.body,
    );
  };

  const renderDropdownMenu = () => {
    if (!showDropdownMenu || !dropdownOptions) return null;

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="filter-dropdown-menu-portal"
        style={{
          position: 'fixed',
          top: `${dropdownCoords.top}px`,
          left: `${dropdownCoords.left}px`,
          zIndex: 99999,
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          minWidth: '200px',
          maxHeight: '300px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div style={{ padding: '8px', borderBottom: '1px solid #ddd', flexShrink: 0 }}>
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="filter-input"
            style={{ width: '100%', margin: 0 }}
            onClick={e => e.stopPropagation()}
          />
        </div>
        <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
          {filteredDropdownOptions.length === 0 ? (
            <div style={{ padding: '8px', color: '#999', textAlign: 'center' }}>No options found</div>
          ) : (
            <>
              {isMultiSelectColumn && (
                <div
                  className="filter-dropdown-option"
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 600,
                    backgroundColor: allFilteredSelected ? '#f0f0f0' : 'transparent',
                    borderBottom: '1px solid #eee',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleSelectAll();
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = allFilteredSelected ? '#f0f0f0' : 'transparent';
                  }}
                >
                  <IndeterminateCheckbox
                    checked={allFilteredSelected}
                    indeterminate={someFilteredSelected}
                    onChange={() => {}}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>Select all</span>
                </div>
              )}
              {filteredDropdownOptions.map(option => (
                <div
                  key={option.id}
                  className="filter-dropdown-option"
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: selectedItems.includes(option.id) ? '#f0f0f0' : 'transparent',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleItem(option.id);
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = selectedItems.includes(option.id)
                      ? '#f0f0f0'
                      : 'transparent';
                  }}
                >
                  <input
                    type={isMultiSelectColumn ? 'checkbox' : 'radio'}
                    checked={selectedItems.includes(option.id)}
                    onChange={() => {}}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>{option.label}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>,
      document.body,
    );
  };

  return (
    <div className="column-filter" onClick={e => e.stopPropagation()}>
      <div
        className={`filter-input-wrapper${supportsEmptinessFilter ? ' filter-input-wrapper-with-menu' : ''}`}
      >
        {filterType === 'select' && dropdownOptions ? (
          <div
            ref={dropdownButtonRef}
            className="filter-input filter-select"
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '4px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              minHeight: '32px',
            }}
            onClick={e => {
              e.stopPropagation();
              setShowDropdownMenu(!showDropdownMenu);
            }}
          >
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedItems.length === 0
                ? 'All'
                : selectedItems.length === 1
                  ? dropdownOptions.find(opt => opt.id === selectedItems[0])?.label || selectedItems[0]
                  : `${selectedItems.length} selected`}
            </span>
          </div>
        ) : (
          <input
            type={filterType === 'number' ? 'number' : 'text'}
            value={value}
            onChange={e => onValueChange(e.target.value)}
            placeholder={
              emptiness === 'not_empty' ? 'Not empty' : emptiness === 'is_empty' ? 'Is empty' : 'Filter...'
            }
            className="filter-input"
          />
        )}
        {(value || emptiness) && (
          <button
            className="filter-clear-button"
            onClick={e => {
              e.stopPropagation();
              onValueChange('');
              setSelectedItems([]);
              if (emptiness && onEmptinessChange) {
                onEmptinessChange('');
              }
            }}
            title="Clear filter"
          >
            ×
          </button>
        )}
        {(filterType !== 'select' || supportsEmptinessFilter) &&
          (operatorOptions.length > 1 || supportsEmptinessFilter) && (
            <div className="filter-menu-wrapper">
              <button
                ref={buttonRef}
                className={`filter-menu-button${emptiness ? ' active' : ''}`}
                onClick={e => {
                  e.stopPropagation();
                  setShowOperatorMenu(!showOperatorMenu);
                }}
                title={
                  emptiness === 'not_empty'
                    ? 'Filter: Not empty'
                    : emptiness === 'is_empty'
                      ? 'Filter: Is empty'
                      : 'Filter operator'
                }
              >
                ⋮
              </button>
            </div>
          )}
      </div>
      {renderMenu()}
      {renderDropdownMenu()}
    </div>
  );
}

function TanStackGrid({
  columns,
  data,
  pagination = true,
  pageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  enableRowSelection = false,
  getRowId,
  manualPagination = false,
  totalCount = 0,
  pageIndex = 0,
  onPageChange,
  onPageSizeChange,
  manualSorting = false,
  onSortingChange,
  sorting = [],
  loading = false,
  showFilters = false,
  filterValues = {},
  filterOperators = {},
  onFilterChange,
  onFilterOperatorChange,
  onBatchFilterChange,
  filterEmptiness = {},
  onFilterEmptinessChange,
  columnOrder: externalColumnOrder,
  onColumnOrderChange,
  columnVisibilityModel: externalColumnVisibilityModel,
  onColumnVisibilityModelChange,
  columnPinning: externalColumnPinning,
  onColumnPinningChange,
  storageKey,
  isAuthenticated,
  searchItems,
  showPopup = false,
  onClosePopup,
  rowDetailColumnCatalog,
  embedded = false,
  onDownload,
  showDownloadShortcut = false,
}: TanStackGridProps) {
  const [isMobileLayout, setIsMobileLayout] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
  const rowSelectionState = controlledRowSelection ?? internalRowSelection;
  const setRowSelectionState = onRowSelectionChange ?? setInternalRowSelection;
  const onRowSelectionChangeRef = useRef(setRowSelectionState);
  onRowSelectionChangeRef.current = setRowSelectionState;
  const handleRowSelectionChange = useCallback((updater: any) => {
    onRowSelectionChangeRef.current(updater);
  }, []);

  const { restrictBrowseAll, groups } = userAppContext();
  const isTrial = groups?.includes('ROLE_TRIAL');
  const { isSidebarOpen } = useRightSidebarLayout();
  const trialBannerMarginRight = !isMobileLayout
    ? isSidebarOpen
      ? 15
      : RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX + 20
    : 0;
  const isAcademic = groups?.includes('ROLE_ACADEMIC');
  const [hoveredHeaderColumn, setHoveredHeaderColumn] = useState<string | null>(null);

  const restrictGridDisplay = !isAuthenticated && !searchItems?.length;
  const downloadLimitLabel = !isAuthenticated
    ? '10 rows'
    : isTrial || restrictBrowseAll
      ? '30 rows'
      : null;
  const canUseDownloadShortcut =
    Boolean(showDownloadShortcut && onDownload) && !restrictGridDisplay && (data?.length ?? 0) > 0;

  const defaultColumnNames =
    embedded && externalColumnOrder && externalColumnOrder.length > 0
      ? externalColumnOrder
      : getAllColumns().map(col => col.name);

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    if (externalColumnVisibilityModel && Object.keys(externalColumnVisibilityModel).length > 0) {
      return externalColumnVisibilityModel;
    }
    return {};
  });

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    return externalColumnOrder && externalColumnOrder.length > 0 ? externalColumnOrder : defaultColumnNames;
  });

  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(() => {
    if (externalColumnPinning) return externalColumnPinning;
    return embedded ? { left: [], right: [] } : { left: [], right: ['coreAction'] };
  });
  const [openHeaderMenuColumn, setOpenHeaderMenuColumn] = useState<string | null>(null);
  const [headerMenuCoords, setHeaderMenuCoords] = useState<MenuCoords>({ top: 0, left: 0 });
  const headerMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const onMediaChange = (event: MediaQueryListEvent) => setIsMobileLayout(event.matches);
    setIsMobileLayout(mediaQuery.matches);
    mediaQuery.addEventListener('change', onMediaChange);
    return () => mediaQuery.removeEventListener('change', onMediaChange);
  }, []);

  const normalizePinning = (pinning?: ColumnPinningState): ColumnPinningState => {
    const left = (pinning?.left ?? []).filter(id => id !== 'coreAction');
    const rightWithoutCore = (pinning?.right ?? []).filter(id => id !== 'coreAction');
    const limitedLeft = left.slice(0, MAX_PINNED_COLUMNS);
    const availableSlots = Math.max(MAX_PINNED_COLUMNS - limitedLeft.length, 0);
    if (embedded) {
      return {
        left: limitedLeft,
        right: rightWithoutCore.slice(0, availableSlots),
      };
    }
    // coreAction always last (rightmost) so user-pinned columns appear to its left
    const limitedRight = [...rightWithoutCore.slice(0, availableSlots), 'coreAction'];
    return { left: limitedLeft, right: limitedRight };
  };

  const prevExternalVisibilityRef = useRef(externalColumnVisibilityModel);

  useEffect(() => {
    if (!externalColumnVisibilityModel || Object.keys(externalColumnVisibilityModel).length === 0) {
      prevExternalVisibilityRef.current = externalColumnVisibilityModel;
      return;
    }
    if (visibilityRecordsEqual(prevExternalVisibilityRef.current, externalColumnVisibilityModel)) {
      return;
    }
    prevExternalVisibilityRef.current = externalColumnVisibilityModel;

    if (!visibilityRecordsEqual(externalColumnVisibilityModel, columnVisibility)) {
      setColumnVisibility(externalColumnVisibilityModel);
      if (storageKey) {
        localStorage.removeItem(`columnVisibility_${storageKey}`);
      }
    }
  }, [externalColumnVisibilityModel, columnVisibility, storageKey]);

  const prevExternalOrderRef = useRef(externalColumnOrder);

  useEffect(() => {
    if (JSON.stringify(prevExternalOrderRef.current) === JSON.stringify(externalColumnOrder)) {
      return;
    }

    prevExternalOrderRef.current = externalColumnOrder;

    if (externalColumnOrder && externalColumnOrder.length > 0) {
      if (JSON.stringify(externalColumnOrder) !== JSON.stringify(columnOrder)) {
        setColumnOrder(externalColumnOrder);
      }
    }
  }, [externalColumnOrder, columns, columnOrder]);

  useEffect(() => {
    const normalized = normalizePinning(externalColumnPinning);
    if (JSON.stringify(normalized) !== JSON.stringify(columnPinning)) {
      setColumnPinning(normalized);
    }
  }, [externalColumnPinning]);

  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const prevColumnsRef = useRef<string[] | undefined>(undefined);
  const isUpdatingOrderRef = useRef<boolean>(false);

  useEffect(() => {
    if (isUpdatingOrderRef.current || draggedColumn) return;

    if (columns && columns.length > 0) {
      const currentColumnIds = columns.map(col => col.name);
      const prevColumnIds = prevColumnsRef.current;

      if (
        !prevColumnIds ||
        JSON.stringify(prevColumnIds.sort()) !== JSON.stringify(currentColumnIds.sort())
      ) {
        if (!externalColumnOrder || externalColumnOrder.length === 0) {
          const hasAllColumns = currentColumnIds.every(id => columnOrder.includes(id));
          const hasSameLength = currentColumnIds.length === columnOrder.length;

          if (!hasAllColumns || !hasSameLength) {
            isUpdatingOrderRef.current = true;
            const newOrder = [
              ...columnOrder.filter(id => currentColumnIds.includes(id)),
              ...currentColumnIds.filter(id => !columnOrder.includes(id)),
            ];
            setColumnOrder(newOrder);
            setTimeout(() => {
              isUpdatingOrderRef.current = false;
            }, 100);
          }
        }
        prevColumnsRef.current = currentColumnIds;
      }
    }
  }, [columns, columnOrder, draggedColumn, externalColumnOrder]);

  const prevColumnOrderRef = useRef(columnOrder);

  useEffect(() => {
    if (JSON.stringify(prevColumnOrderRef.current) !== JSON.stringify(columnOrder)) {
      prevColumnOrderRef.current = columnOrder;

      if (onColumnOrderChange && !isUpdatingOrderRef.current) {
        onColumnOrderChange(columnOrder);
      }
    }
  }, [columnOrder, onColumnOrderChange, externalColumnOrder]);

  const prevReportedPinningRef = useRef<string>('');

  useEffect(() => {
    if (!onColumnPinningChange) return;
    const serialized = JSON.stringify(columnPinning);
    if (prevReportedPinningRef.current === serialized) return;
    prevReportedPinningRef.current = serialized;
    onColumnPinningChange(columnPinning);
  }, [columnPinning, onColumnPinningChange]);

  const handleDragStart = (e: React.DragEvent<HTMLSpanElement>, columnId: string) => {
    setDraggedColumn(columnId);
    setDragOverColumn(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableCellElement>, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedColumn && draggedColumn !== columnId) {
      setDragOverColumn(prev => (prev === columnId ? prev : columnId));
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>, targetColumnId: string) => {
    e.preventDefault();

    const currentOrder = table.getState().columnOrder;
    const draggedIndex = currentOrder.indexOf(draggedColumn);
    const targetIndex = currentOrder.indexOf(targetColumnId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      const newOrder = [...currentOrder];
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedColumn);
      table.setColumnOrder(newOrder);
    }
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const selectColumn: ColumnDef<any> = useMemo(
    () => ({
      id: 'select',
      size: 44,
      minSize: 44,
      maxSize: 44,
      enableSorting: false,
      enableColumnFilter: false,
      enableResizing: false,
      header: ({ table }: any) => (
        <IndeterminateCheckbox
          checked={table.getIsAllPageRowsSelected()}
          indeterminate={table.getIsSomePageRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
        />
      ),
      cell: ({ row }: any) => (
        <div className="px-1" onClick={e => e.stopPropagation()}>
          <IndeterminateCheckbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        </div>
      ),
    }),
    [],
  );

  const openRowDetails = useCallback(
    (
      row: any,
      tableInstance: any,
      options?: {
        initialTab?: 'summary' | 'historical' | 'gtp';
        gtpOnly?: boolean;
        gtpOverride?: unknown;
      },
    ) => {
      const values: Record<string, any> = {};
      const labels: Record<string, string> = {};
      const categories: Record<string, string> = {};
      const orig = row.original as Record<string, any> | undefined;
      if (rowDetailColumnCatalog && rowDetailColumnCatalog.length > 0) {
        const mountedColumnIds = new Set(
          tableInstance.getAllLeafColumns().map((column: { id: string }) => column.id),
        );
        rowDetailColumnCatalog.forEach(({ id, displayLabel, category, valueGetter }) => {
          if (id === 'coreAction' || id === 'select' || id === 'text') return;
          if (valueGetter) {
            values[id] = valueGetter(orig);
          } else if (mountedColumnIds.has(id)) {
            values[id] = row.getValue(id);
          } else {
            values[id] = orig?.[id];
          }
          labels[id] = displayLabel;
          if (category) categories[id] = category;
        });
      } else {
        tableInstance.getAllLeafColumns().forEach((column: { id: string; columnDef: ColumnDef<any> }) => {
          const colId = column.id;
          if (colId === 'coreAction' || colId === 'select' || colId === 'text') return;
          values[colId] = row.getValue(colId);
          const meta = column.columnDef.meta as { displayLabel?: string; columnGroup?: string } | undefined;
          labels[colId] = meta?.displayLabel ?? colId;
          if (meta?.columnGroup) categories[colId] = meta.columnGroup;
        });
      }

      Object.keys(values).forEach(id => {
        if (!isNcbiIdColumn(id)) return;
        values[id] = getRowGeneNcbiId(orig) ?? values[id];
      });

      const gtpOverrideItems = getGtpItemsFromRow({ GTP: options?.gtpOverride } as Record<string, unknown>);
      const originalWithGtp =
        gtpOverrideItems.length > 0
          ? { ...(row.original as Record<string, unknown>), GTP: options?.gtpOverride }
          : row.original;
      const valuesWithGtp = gtpOverrideItems.length > 0 ? { ...values, GTP: options?.gtpOverride } : values;

      window.dispatchEvent(
        new CustomEvent('disgenet:open-bottom-row-detail', {
          detail: {
            original: originalWithGtp,
            values: valuesWithGtp,
            labels,
            categories,
            ...(options?.initialTab ? { initialTab: options.initialTab } : {}),
            ...(options?.gtpOnly ? { gtpOnly: true } : {}),
          },
        }),
      );
    },
    [rowDetailColumnCatalog],
  );

  const tableColumns = useMemo<ColumnDef<any>[]>(() => {
    if (!columns) return [];

    const columnNames = columns.map(col => (col.name || '').toLowerCase());
    const inferredAssociationGroup = (() => {
      const hasVariant = columnNames.some(
        name => name.includes('variant') || name.includes('dbsnp') || name.includes('hgv'),
      );
      const diseaseLikeColumns = columnNames.filter(name => name.includes('disease'));
      if (hasVariant) return 'VDA';
      if (diseaseLikeColumns.length > 1) return 'DDA';
      return 'GDA';
    })();

    const getColumnGroupLabel = (columnName?: string) => {
      const name = (columnName || '').toLowerCase();
      if (!name) return inferredAssociationGroup;
      if (name.includes('chem')) return 'Chemical';
      if (name.includes('variant') || name.includes('dbsnp') || name.includes('hgv')) return 'Variant';
      if (name.includes('gene')) return 'Gene';
      if (name.includes('disease')) return 'Disease';
      return inferredAssociationGroup;
    };

    const dataColumns = columns.map(col => ({
      id: col.name,
      ...(typeof col.accessorFn === 'function' ? { accessorFn: col.accessorFn } : { accessorKey: col.name }),
      header: () => {
        if (React.isValidElement(col.header)) {
          return col.header;
        }
        if (typeof col.header === 'string') {
          return col.header;
        }
        return col.name;
      },
      cell: col.render
        ? (info: any) => {
            try {
              if (!info.row.original) return null;
              const result = col.render({
                data: info.row.original,
                value: info.getValue(),
                openRowDetails: (options?: {
                  initialTab?: 'summary' | 'historical' | 'gtp';
                  gtpOnly?: boolean;
                }) => openRowDetails(info.row, info.table, options),
              });
              return result;
            } catch {
              // Silent error handling - return empty string for failed renders
              return '';
            }
          }
        : (info: any) => {
            const value = info.getValue();
            return value != null ? value : '';
          },
      enableSorting: col.sortable !== false,
      enableColumnFilter: col.enableColumnFilter !== false,
      size: col.minWidth || 150,
      minSize: col.minWidth || 100,
      maxSize: 1500,
      meta: {
        filterType: col.filterType || (col.type === 'number' ? 'number' : 'text'),
        dropdownOptions: col.dropdownOptions,
        columnGroup: (col as any).category || getColumnGroupLabel(col.name),
        displayLabel: typeof col.header === 'string' ? col.header : col.name,
      },
    }));

    const coreActionColumn: ColumnDef<any> = {
      id: 'coreAction',
      size: 72,
      minSize: 72,
      maxSize: 72,
      enableSorting: false,
      enableColumnFilter: false,
      enableResizing: false,
      header: () => <span title="Row actions"></span>,
      cell: ({ row, table }: any) => (
        <div className="flex items-center justify-center gap-0.5">
          <RowAidaQuestionsButton row={(row.original ?? {}) as Record<string, unknown>} />
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              trackEvent({
                name: 'row_details_open',
                buttonId: 'table_open_row_details',
                label: 'Open row details',
                page: 'search_results',
                section: 'results_table',
                feature: 'table',
                eventCategory: 'table',
              });
              openRowDetails(row, table);
            }}
            className="tab-links px-0.5"
            title="Open row details"
            data-demo-tour="open-row-detail"
          >
            <ChevronRightIcon className="w-4 h-4" strokeWidth={2} color="gray" />
          </button>
        </div>
      ),
      meta: { columnGroup: 'Other' },
    };

    if (embedded) {
      return enableRowSelection ? [selectColumn, ...dataColumns] : [...dataColumns];
    }

    return enableRowSelection
      ? [selectColumn, ...dataColumns, coreActionColumn]
      : [...dataColumns, coreActionColumn];
  }, [columns, embedded, enableRowSelection, selectColumn, openRowDetails]);

  const validColumnOrder = useMemo(() => {
    if (!tableColumns || tableColumns.length === 0) return [];
    if (!columnOrder || columnOrder.length === 0) {
      return tableColumns.map(col => col.id as string);
    }

    const currentColumnIds = tableColumns.map(col => col.id as string);
    const isValid =
      columnOrder.length === currentColumnIds.length &&
      columnOrder.every(id => currentColumnIds.includes(id));

    let validOrder: string[];
    if (isValid) {
      validOrder = columnOrder;
    } else {
      const validOrderFiltered = columnOrder.filter(id => currentColumnIds.includes(id));
      const missingColumns = currentColumnIds.filter(id => !columnOrder.includes(id));
      validOrder = [...validOrderFiltered, ...missingColumns];
    }
    if (enableRowSelection && currentColumnIds.includes('select')) {
      validOrder = ['select', ...validOrder.filter(id => id !== 'select')];
    }
    if (currentColumnIds.includes('coreAction')) {
      validOrder = [...validOrder.filter(id => id !== 'coreAction'), 'coreAction'];
    }
    return validOrder;
  }, [columnOrder, tableColumns, enableRowSelection]);

  const table = useReactTable({
    data: data || [],
    columns: tableColumns,
    getRowId: getRowId || undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: manualPagination ? undefined : pagination ? getPaginationRowModel() : undefined,
    manualPagination: manualPagination,
    manualSorting: manualSorting,
    pageCount: manualPagination ? Math.ceil(totalCount / pageSize) : undefined,
    enableColumnResizing: true,
    columnResizeMode: embedded ? 'onEnd' : 'onChange',
    ...(pagination && !manualPagination
      ? {
          initialState: {
            pagination: { pageIndex: 0, pageSize },
          },
        }
      : {}),
    state: {
      ...(manualPagination && pagination
        ? {
            pagination: {
              pageIndex: pageIndex,
              pageSize: pageSize,
            },
          }
        : {}),
      ...(manualSorting ? { sorting } : {}),
      columnOrder: validColumnOrder,
      columnSizing: columnSizing,
      columnVisibility: embedded ? columnVisibility : { ...columnVisibility, coreAction: true },
      columnPinning: normalizePinning(columnPinning),
      ...(enableRowSelection ? { rowSelection: rowSelectionState } : {}),
    },
    onColumnSizingChange: setColumnSizing,
    onPaginationChange: manualPagination
      ? (updater: any) => {
          const newState: PaginationState =
            typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
          if (newState.pageIndex !== pageIndex && onPageChange) {
            onPageChange(newState.pageIndex);
          }
          if (newState.pageSize !== pageSize && onPageSizeChange) {
            onPageSizeChange(newState.pageSize);
          }
        }
      : undefined,
    onSortingChange: manualSorting ? onSortingChange : undefined,
    onColumnOrderChange: (newOrder: string[]) => {
      setColumnOrder(newOrder);
      if (onColumnOrderChange) {
        onColumnOrderChange(newOrder);
      }
    },
    onColumnPinningChange: updater => {
      const next = typeof updater === 'function' ? updater(columnPinning) : updater;
      setColumnPinning(normalizePinning(next));
    },
    onColumnVisibilityChange: updater => {
      const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
      const resolved = embedded ? next : { ...next, coreAction: true };
      setColumnVisibility(resolved);
      onColumnVisibilityModelChange?.(resolved);
    },
    enableRowSelection: enableRowSelection,
    onRowSelectionChange: enableRowSelection ? handleRowSelectionChange : undefined,
    autoResetAll: false,
  });

  useEffect(() => {
    if (embedded) return;

    const handleOpenGtpRowDetailForGene = (event: Event) => {
      const detail = (event as CustomEvent<{ geneId?: string; gtp?: unknown }>).detail ?? {};
      const geneId = String(detail.geneId ?? '').trim();
      if (!geneId) return;
      const match = table.getCoreRowModel().rows.find(row => rowMatchesGeneId(row.original, geneId));
      if (!match) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openRowDetails(match, table, {
        initialTab: 'gtp',
        gtpOnly: true,
        ...(getGtpItemsFromRow({ GTP: detail.gtp } as Record<string, unknown>).length > 0
          ? { gtpOverride: detail.gtp }
          : {}),
      });
    };

    window.addEventListener(OPEN_GTP_ROW_DETAIL_FOR_GENE_EVENT, handleOpenGtpRowDetailForGene);
    return () => {
      window.removeEventListener(OPEN_GTP_ROW_DETAIL_FOR_GENE_EVENT, handleOpenGtpRowDetailForGene);
    };
  }, [embedded, openRowDetails, table, data]);

  const visibleLeafColumns = table
    .getHeaderGroups()
    .flatMap(group => group.headers)
    .filter(header => !header.isPlaceholder)
    .map(header => header.column);
  const pinnedCount = embedded
    ? (columnPinning.left?.length ?? 0) + (columnPinning.right?.length ?? 0)
    : (columnPinning.left?.length ?? 0) + Math.max((columnPinning.right?.length ?? 1) - 1, 0);
  const groupSegments = useMemo(() => {
    const segments: Array<{ key: string; label: string; span: number }> = [];
    visibleLeafColumns.forEach(col => {
      const label = (col.columnDef.meta as any)?.columnGroup || 'Other';
      const last = segments[segments.length - 1];
      if (last && last.label === label) {
        last.span += 1;
      } else {
        segments.push({ key: `${label}-${segments.length}`, label, span: 1 });
      }
    });
    return segments;
  }, [visibleLeafColumns]);

  const getPinnedOffset = (column: any): number => {
    const isPinned = column.getIsPinned?.();
    if (!isPinned) return 0;
    if (isPinned === 'left') return column.getStart?.('left') ?? 0;
    // For right-pinned: use getAfter which gives distance from right edge
    return column.getAfter?.('right') ?? 0;
  };

  const getPinnedStyles = (column: any): React.CSSProperties => {
    const isPinned = column.getIsPinned?.();
    if (!isPinned) return {};
    const side = isPinned === 'left' ? 'left' : 'right';
    // pinned-side border + header bottom border
    const shadow =
      side === 'left'
        ? 'inset -2px 0 0 #d4d4d4, inset 0 -1px 0 #e0e0e0'
        : 'inset 2px 0 0 #d4d4d4, inset 0 -1px 0 #e0e0e0';
    return {
      position: 'sticky',
      [side]: `${getPinnedOffset(column)}px`,
      zIndex: 12,
      boxShadow: shadow,
    };
  };

  const getPinnedStylesColumnContent = (column: any): React.CSSProperties => {
    const isPinned = column.getIsPinned?.();
    if (!isPinned) return {};
    const side = isPinned === 'left' ? 'left' : 'right';
    // row-bottom border + pinned-side border combined so sticky cells look correct
    const shadow =
      side === 'left'
        ? 'inset -2px 0 0 #d4d4d4, inset 0 -1px 0 #d4d4d4'
        : 'inset 2px 0 0 #d4d4d4, inset 0 -1px 0 #d4d4d4';
    return {
      position: 'sticky',
      [side]: `${getPinnedOffset(column)}px`,
      zIndex: 4,
      backgroundColor: '#fff',
      boxShadow: shadow,
    };
  };

  const handleHeaderMenuAction = (columnId: string, action: 'pin-left' | 'pin-right' | 'unpin' | 'hide') => {
    if (columnId === 'coreAction') return;
    if (action === 'hide') {
      const columnExists =
        table.getAllLeafColumns?.().some(col => (col as any).id === columnId) ||
        table.getAllColumns?.().some(col => (col as any).id === columnId);

      if (columnExists) {
        try {
          table.getColumn(columnId)?.toggleVisibility(false);
          const nextVisibility = {
            ...columnVisibility,
            [columnId]: false,
            coreAction: true,
          };
          setColumnVisibility(nextVisibility);
          onColumnVisibilityModelChange?.(nextVisibility);
        } catch {
          // No-op: column might have been removed between checks.
        }
      }
      setOpenHeaderMenuColumn(null);
      return;
    }

    const next = normalizePinning(columnPinning);
    const removeFromLeft = (next.left ?? []).filter(id => id !== columnId);
    const removeFromRight = (next.right ?? []).filter(id => id !== columnId && id !== 'coreAction');
    if (action === 'unpin') {
      setColumnPinning(normalizePinning({ left: removeFromLeft, right: removeFromRight }));
      setOpenHeaderMenuColumn(null);
      return;
    }
    const candidate =
      action === 'pin-left'
        ? normalizePinning({ left: [...removeFromLeft, columnId], right: removeFromRight })
        : normalizePinning({ left: removeFromLeft, right: [...removeFromRight, columnId] });
    setColumnPinning(candidate);
    setOpenHeaderMenuColumn(null);
  };

  useEffect(() => {
    if (!openHeaderMenuColumn) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (isEventInsideDemoTourTip(event)) return;
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target as Node)) {
        setOpenHeaderMenuColumn(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openHeaderMenuColumn]);

  const renderHeaderMenu = () => {
    if (!openHeaderMenuColumn || openHeaderMenuColumn === 'coreAction') return null;
    const pinnedCandidate =
      table.getAllLeafColumns?.().find(col => (col as any).id === openHeaderMenuColumn) ||
      table.getAllColumns?.().find(col => (col as any).id === openHeaderMenuColumn);
    const isPinned: string | boolean | undefined = (pinnedCandidate as any)?.getIsPinned?.();
    return ReactDOM.createPortal(
      <div
        ref={headerMenuRef}
        data-demo-tour="column-options-menu"
        style={{
          position: 'fixed',
          top: `${headerMenuCoords.top}px`,
          left: `${headerMenuCoords.left}px`,
          zIndex: 100000,
        }}
        className="min-w-28 rounded-xl border bg-white p-2 text-xs shadow"
      >
        <button
          type="button"
          data-demo-tour="column-pin"
          className="w-full px-2 py-1 text-left hover:bg-pink-200 rounded-xl text-xs flex items-center font-semibold mb-2"
          onClick={() => handleHeaderMenuAction(openHeaderMenuColumn, 'pin-left')}
        >
          <img src="/static/images/icons/pinIcon.svg" alt="pin left" className="w-4 h-4 mr-1" />
          Pin to left
        </button>
        <button
          type="button"
          className="w-full px-2 py-1 text-left hover:bg-pink-200 rounded-xl text-xs flex items-center font-semibold mb-2"
          onClick={() => handleHeaderMenuAction(openHeaderMenuColumn, 'pin-right')}
        >
          <img
            src="/static/images/icons/pinIcon.svg"
            alt="pin left"
            className="w-4 h-4 mr-1"
            style={{ transform: 'rotate(90deg)' }}
          />
          Pin to right
        </button>
        {isPinned && (
          <button
            type="button"
            className="w-full px-2 py-1 text-left hover:bg-pink-200 rounded-xl text-xs flex items-center font-semibold mb-2"
            onClick={() => handleHeaderMenuAction(openHeaderMenuColumn, 'unpin')}
          >
            <img
              src="/static/images/icons/pinOffIcon.svg"
              alt="unpin column"
              className="w-4 h-4 mr-1 opacity-60"
            />
            Unpin column
          </button>
        )}
        <span className="block w-full h-px bg-slate-200 my-2 border-b border-slate-200"></span>
        <button
          type="button"
          data-demo-tour="column-hide"
          className="w-full px-2 py-1 text-left hover:bg-pink-200 rounded-xl text-xs flex items-center font-semibold mb-2"
          onClick={() => handleHeaderMenuAction(openHeaderMenuColumn, 'hide')}
        >
          <EyeSlashIcon className="w-4 h-4 mr-1" />
          Hide column
        </button>
      </div>,
      document.body,
    );
  };

  const safeTotalSize = typeof table.getTotalSize === 'function' ? table.getTotalSize() : 900;
  const safeVisibleLeafColumns =
    typeof table.getVisibleLeafColumns === 'function' ? table.getVisibleLeafColumns() : [];
  const safeLeftColumns =
    typeof table.getLeftVisibleLeafColumns === 'function' ? table.getLeftVisibleLeafColumns() : [];
  const safeCenterColumns =
    typeof table.getCenterVisibleLeafColumns === 'function'
      ? table.getCenterVisibleLeafColumns()
      : safeVisibleLeafColumns;
  const safeRightColumns =
    typeof table.getRightVisibleLeafColumns === 'function' ? table.getRightVisibleLeafColumns() : [];
  const orderedVisibleColumns = [...safeLeftColumns, ...safeCenterColumns, ...safeRightColumns];

  return (
    <>
      {!embedded && (
        <div className="flex items-center justify-between gap-4 text-xs text-slate-600 ml-3 mb-2 mt-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-semibold tracking-wide">
              {manualPagination ? totalCount : (data?.length ?? 0)}{' '}
              <span className="font-normal ml-1 text-gray-500">rows</span>
            </span>
            {canUseDownloadShortcut && onDownload && (
              <>
                <span aria-hidden>•</span>
                <TableHeaderDownload
                  onDownload={onDownload}
                  limitLabel={downloadLimitLabel}
                  disabled={restrictGridDisplay}
                />
              </>
            )}
            <span>•</span>
            <span className="font-semibold tracking-wide">
              {visibleLeafColumns.length}{' '}
              <span className="font-normal ml-1 text-gray-500">columns visible</span>
            </span>
            <span>•</span>
            <span className="tracking-wide text-pink flex items-center gap-1">
              <img src="/static/images/icons/pinIcon.svg" alt="pinned" className="w-3 h-3 mr-1" />{' '}
              {pinnedCount} pinned
            </span>
          </div>
          {isTrial && (
            <div
              style={{
                marginRight: trialBannerMarginRight,
                transition: 'margin-right 0.3s ease-out',
              }}
            >
              <p className="text-xs text-slate-600 mb-0">
                Only the top 30 results are shown with your current plan.{' '}
                <a href="/plans" className="text-pink font-semibold">
                  View our plans
                </a>{' '}
                to upgrade your account.
              </p>
            </div>
          )}
          {isAcademic && (
            <div
              style={{
                marginRight: trialBannerMarginRight,
                transition: 'margin-right 0.3s ease-out',
              }}
            >
              <p className="mb-0 text-xs text-slate-600">
                Some columns are not available in your current academic plan.{' '}
                <a
                  href="/plans"
                  className="text-pink font-semibold underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Upgrade your plan
                </a>
                .
              </p>
            </div>
          )}
        </div>
      )}
      <div
        className={
          embedded ? 'tanstack-grid-container tanstack-grid-container--embedded' : 'tanstack-grid-container'
        }
      >
        {!loading && (!data || data.length === 0) && (
          <div className="tanstack-empty-state">
            <div className="empty-state-icon">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M9 2v6h6V2M19 2v6M5 2v6M3 10h18M5 22h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                <path d="M7 14h.01M12 14h.01M17 14h.01M7 18h.01M12 18h.01M17 18h.01" />
              </svg>
            </div>
            <h3 className="empty-state-title">No results found</h3>
            <p className="empty-state-message">Try adjusting your search or filter criteria.</p>
          </div>
        )}
        {loading && (
          <>
            <div className="tanstack-loading-state">
              <div className="loader-spinner"></div>
              <p>Loading data...</p>
            </div>
          </>
        )}
        <div
          className={
            embedded ? 'tanstack-grid-wrapper tanstack-grid-wrapper--embedded' : 'tanstack-grid-wrapper'
          }
          style={
            embedded
              ? {
                  overflowX: 'auto',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-x pan-y',
                }
              : {
                  maxHeight: isMobileLayout ? '72vh' : '660px',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-x pan-y',
                }
          }
        >
          <table
            className="tanstack-grid"
            style={{
              opacity: loading ? 0.5 : 1,
              borderCollapse: 'separate',
              borderSpacing: 0,
              tableLayout: 'fixed',
              width: Math.max(safeTotalSize, 900),
              minWidth: '100%',
            }}
          >
            <colgroup>
              {orderedVisibleColumns.map(column => (
                <col
                  key={column.id}
                  style={{
                    width: `${typeof column.getSize === 'function' ? column.getSize() : 150}px`,
                  }}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {groupSegments.map(seg => (
                  <th
                    key={seg.key}
                    colSpan={seg.span}
                    style={{
                      top: 0,
                      zIndex: 14,
                      borderBottom: '0.5px solid #e0e0e0',
                      color: 'var(--terciary-gray-color)',
                      fontWeight: '500',
                    }}
                    className={`text-xs uppercase bg-white category-header-${seg.label.toLowerCase()}`}
                  >
                    <span className="mr-2 text-md">•</span>
                    {seg.label}
                  </th>
                ))}
              </tr>
              {table.getHeaderGroups().map(headerGroup => (
                <tr
                  key={headerGroup.id}
                  onDrop={e => {
                    e.preventDefault();
                    if (draggedColumn && dragOverColumn && draggedColumn !== dragOverColumn) {
                      handleDrop(e, dragOverColumn);
                    } else {
                      setDraggedColumn(null);
                      setDragOverColumn(null);
                    }
                  }}
                  onDragOver={e => e.preventDefault()}
                >
                  {headerGroup.headers.map(header => {
                    const isPinned = header.column.getIsPinned?.() ?? false;

                    return (
                      <th
                        key={header.id}
                        style={{
                          minWidth: header.column.columnDef.minSize,
                          width: header.getSize(),
                          maxWidth: header.column.id === 'coreAction' ? 10 : undefined,
                          padding: header.column.id === 'coreAction' ? 0 : undefined,
                          position: 'sticky',
                          top: 24,
                          backgroundColor: isPinned
                            ? 'color-mix(in oklab,oklch(96% .02 290) 100%,transparent)'
                            : 'color-mix(in oklab,oklch(96% .02 290) 60%,transparent)',
                          zIndex: isPinned ? 20 : 10,
                          ...getPinnedStyles(header.column),
                        }}
                        onDragOver={e => handleDragOver(e, header.column.id)}
                        onDrop={e => handleDrop(e, header.column.id)}
                        className={`
                        ${draggedColumn === header.column.id ? 'dragging-column' : ''}
                        ${dragOverColumn === header.column.id ? 'drag-over-column' : ''}
                      `.trim()}
                      >
                        {header.isPlaceholder ? null : (
                          <>
                            <div
                              className={`grid-header-content ${
                                header.column.id === 'coreAction'
                                  ? 'justify-center'
                                  : header.column.id !== 'select' && header.column.getCanSort()
                                    ? 'sortable-header'
                                    : ''
                              }`}
                              draggable={
                                !isMobileLayout &&
                                header.column.id !== 'select' &&
                                header.column.id !== 'coreAction'
                              }
                              onDragStart={
                                header.column.id !== 'select' && header.column.id !== 'coreAction'
                                  ? e => handleDragStart(e, header.column.id)
                                  : undefined
                              }
                              onDragEnd={
                                header.column.id !== 'select' && header.column.id !== 'coreAction'
                                  ? handleDragEnd
                                  : undefined
                              }
                              onClick={e => {
                                if (header.column.id === 'select') return;
                                if (!draggedColumn && header.column.getCanSort()) {
                                  const sortHandler = header.column.getToggleSortingHandler();
                                  if (sortHandler) sortHandler(e);
                                }
                              }}
                              onMouseEnter={() =>
                                setHoveredHeaderColumn(prev =>
                                  prev === header.column.id ? prev : header.column.id,
                                )
                              }
                              onMouseLeave={() =>
                                setHoveredHeaderColumn(prev => (prev === header.column.id ? null : prev))
                              }
                            >
                              <div className="flex items-center gap-2">
                                {isPinned && header.column.id !== 'coreAction' && (
                                  <img
                                    src="/static/images/icons/pinIcon.svg"
                                    alt="pinned"
                                    className="w-4 h-4 mr-1"
                                  />
                                )}

                                <span className="text-xs uppercase tracking-widest font-medium">
                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                </span>
                              </div>

                              <div>
                                {header.column.id !== 'select' && header.column.id !== 'coreAction' && (
                                  <span
                                    className="drag-handle inline-flex w-4 justify-center"
                                    title="Drag to reorder"
                                    data-demo-tour="column-drag-handle"
                                    style={{
                                      visibility:
                                        isMobileLayout || hoveredHeaderColumn === header.column.id
                                          ? 'visible'
                                          : 'hidden',
                                    }}
                                  >
                                    ⋮⋮
                                  </span>
                                )}
                                {header.column.id !== 'select' && header.column.id !== 'coreAction' && (
                                  <div className="relative ml-1 inline-flex w-5 justify-center">
                                    <button
                                      type="button"
                                      onClick={e => {
                                        e.stopPropagation();
                                        trackEvent({
                                          name: 'column_options_open',
                                          buttonId: 'table_column_options_menu',
                                          label: 'Open column options menu',
                                          page: 'search_results',
                                          section: 'results_table_header',
                                          feature: 'table_columns',
                                          eventCategory: 'table',
                                        });
                                        const rect = (
                                          e.currentTarget as HTMLButtonElement
                                        ).getBoundingClientRect();
                                        setHeaderMenuCoords({ top: rect.bottom + 4, left: rect.right - 120 });
                                        setOpenHeaderMenuColumn(prev =>
                                          prev === header.column.id ? null : header.column.id,
                                        );
                                      }}
                                      className="px-1 text-slate-500"
                                      title="Column options"
                                      data-demo-tour="column-options"
                                      style={{
                                        visibility:
                                          isMobileLayout || hoveredHeaderColumn === header.column.id
                                            ? 'visible'
                                            : 'hidden',
                                        pointerEvents:
                                          isMobileLayout || hoveredHeaderColumn === header.column.id
                                            ? 'auto'
                                            : 'none',
                                      }}
                                    >
                                      ⋯
                                    </button>
                                  </div>
                                )}

                                {header.column.id !== 'select' && header.column.getIsSorted() && (
                                  <span className="sort-indicator">
                                    {header.column.getIsSorted() === 'asc' ? ' ↑' : ' ↓'}
                                  </span>
                                )}
                              </div>
                            </div>
                            {showFilters &&
                              header.column.id !== 'select' &&
                              header.column.getCanFilter() &&
                              (header.column.id === 'evidenceTimeline' ? (
                                <EvidenceTimelineColumnFilter
                                  filterValues={filterValues}
                                  filterOperators={filterOperators}
                                  onFilterChange={onFilterChange}
                                  onFilterOperatorChange={onFilterOperatorChange}
                                  onBatchFilterChange={onBatchFilterChange}
                                />
                              ) : header.column.id === 'GTP' ? (
                                <GtpColumnFilter
                                  filterValues={filterValues}
                                  filterOperators={filterOperators}
                                  dropdownOptions={(header.column.columnDef.meta as any)?.dropdownOptions}
                                  onFilterChange={onFilterChange}
                                  onFilterOperatorChange={onFilterOperatorChange}
                                  onBatchFilterChange={onBatchFilterChange}
                                />
                              ) : (
                                <ColumnFilter
                                  columnId={header.column.id}
                                  filterType={(header.column.columnDef.meta as any)?.filterType || 'text'}
                                  value={filterValues[header.column.id] || ''}
                                  operator={filterOperators[header.column.id]}
                                  onValueChange={value =>
                                    onFilterChange && onFilterChange(header.column.id, value)
                                  }
                                  onOperatorChange={op =>
                                    onFilterOperatorChange && onFilterOperatorChange(header.column.id, op)
                                  }
                                  dropdownOptions={(header.column.columnDef.meta as any)?.dropdownOptions}
                                  emptiness={filterEmptiness[header.column.id] as any}
                                  onEmptinessChange={
                                    onFilterEmptinessChange
                                      ? val => onFilterEmptinessChange(header.column.id, val)
                                      : undefined
                                  }
                                />
                              ))}
                            {header.column.id !== 'select' && header.column.id !== 'coreAction' && (
                              <div
                                onMouseDown={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  header.getResizeHandler()(e);
                                }}
                                onTouchStart={e => {
                                  e.stopPropagation();
                                  header.getResizeHandler()(e);
                                }}
                                onClick={e => e.stopPropagation()}
                                onDragStart={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                              />
                            )}
                          </>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {data &&
                data.length > 0 &&
                table.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          maxWidth: cell.column.id === 'coreAction' ? 10 : undefined,
                          padding: cell.column.id === 'coreAction' ? 0 : undefined,
                          ...getPinnedStylesColumnContent(cell.column),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {pagination && (
          <>
            <div className="tanstack-pagination">
              <div className="pagination-info">
                {manualPagination ? (
                  <>
                    Showing {formatNumber(pageIndex * pageSize + 1)} to{' '}
                    {formatNumber(Math.min((pageIndex + 1) * pageSize, totalCount))} of{' '}
                    {formatNumber(totalCount)} results
                  </>
                ) : (
                  <>
                    Showing{' '}
                    {formatNumber(
                      table.getState().pagination!.pageIndex * table.getState().pagination!.pageSize + 1,
                    )}{' '}
                    to{' '}
                    {formatNumber(
                      Math.min(
                        (table.getState().pagination!.pageIndex + 1) * table.getState().pagination!.pageSize,
                        table.getFilteredRowModel().rows.length,
                      ),
                    )}{' '}
                    of {formatNumber(table.getFilteredRowModel().rows.length)} results
                  </>
                )}
              </div>

              <div className="pagination-controls">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage() || restrictGridDisplay}
                >
                  {'<<'}
                </button>
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage() || restrictGridDisplay}
                >
                  {'<'}
                </button>
                <span className="page-info">
                  Page{' '}
                  {formatNumber((manualPagination ? pageIndex : table.getState().pagination!.pageIndex) + 1)}{' '}
                  of {formatNumber(table.getPageCount())}
                </span>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage() || restrictGridDisplay}
                >
                  {'>'}
                </button>
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage() || restrictGridDisplay}
                >
                  {'>>'}
                </button>
              </div>

              <select
                value={manualPagination ? pageSize : table.getState().pagination!.pageSize}
                onChange={e => {
                  const newSize = Number(e.target.value);
                  if (manualPagination && onPageSizeChange) {
                    onPageSizeChange(newSize);
                  } else {
                    table.setPageSize(newSize);
                  }
                }}
                className="page-size-select"
                disabled={restrictGridDisplay}
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-center text-gray-500 py-1">
              <small className="flex items-center justify-center">
                <img src="/static/images/icons/pinIcon.svg" alt="info" className="w-3 h-3 mr-1" />
                You can <strong className="mx-1">pin/unpin</strong> columns by clicking on the column header 3
                dots.
              </small>
            </div>
          </>
        )}
        {showPopup && (
          <PopupMessage
            isAuthenticated={isAuthenticated}
            restrictBrowseAll={restrictBrowseAll}
            isTrial={isTrial}
            onClose={onClosePopup}
            contentClass="w-1/2"
          />
        )}
        {renderHeaderMenu()}
      </div>
    </>
  );
}

export default TanStackGrid;
